import React, { useState, useContext, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2, Edit2, GripVertical, ArrowRight } from 'lucide-react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import MiniWaves from '@/components/animations/MiniWaves'
import { ProjectContext } from '../../../context/ProjectContext'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import { useChatStore } from '@/components/askAnything/chatStore'
import toast from 'react-hot-toast'

// const SCRIPT_SECTIONS = [
//   { id: 'intro', title: '👋 Intro' },
//   { id: 'warmup', title: '🌤️ Warm-Up' },
//   { id: 'primary', title: '🎯 Primary Questions' },
//   { id: 'final', title: '⏳ Final Questions' },
//   { id: 'wrapup', title: '🎬 Wrap-Up' }
// ]

// const DEFAULT_SCRIPT_ITEMS = {
//   intro: [
//     'Thank Participant',
//     'Introduce Yourself',
//     'Explain Reason for Meeting',
//     'Set Expectations',
//     'Request Buy-In'
//   ],
//   warmup: [
//     'Role & Responsibilities Question',
//     'How Long Have You Been Doing This?'
//   ],
//   primary: [],
//   final: [],
//   wrapup: [
//     'Thank them for time and insights.',
//     'Say feedback is valuable.',
//     'May reach out to you again. OK?',
//     'Any questions for us before we conclude?'
//   ]
// }

export function ResearchQuestions() {
  const { project } = useContext(ProjectContext)
  const customFetch = useCustomFetch()
  const { selectedProduct, addToNewChatThread } = useChatStore()
  const navigate = useNavigate()
  const [findOutItems, setFindOutItems] = useState([])
  const [newFindOutItem, setNewFindOutItem] = useState('')
  const [editingItem, setEditingItem] = useState({ type: null, index: null, value: '', section: null })
  const [saving, setSaving] = useState(false)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const lastSavedItems = useRef([])
  const projectIdRef = useRef(project.id)

  // Add console log to debug project properties
  useEffect(() => {
    console.log('Project object:', project)
    console.log('Project product value:', project.product)
  }, [project])

  // Load research questions ONCE when the component mounts
  // or ONLY when project ID changes
  useEffect(() => {
    // Skip if project ID hasn't changed to avoid unnecessary fetches
    if (projectIdRef.current === project.id && initialLoadComplete) return
    projectIdRef.current = project.id

    async function fetchQuestions() {
      if (!project.id) return

      try {
        const response = await customFetch('/getResearchQuestions', 'POST', {
          projectId: project.id,
        })

        if (response && response.questions) {
          // Sort by position and extract content
          const sortedQuestions = response.questions.sort((a, b) => a.position - b.position).map((q) => q.content)

          setFindOutItems(sortedQuestions)
          lastSavedItems.current = sortedQuestions
          setInitialLoadComplete(true)
        }
      } catch (error) {
        console.error('Error fetching research questions:', error)
      }
    }

    fetchQuestions()
    // Note: customFetch is intentionally omitted from the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id])

  // Save research questions only when they've been edited
  // and are different from last saved state
  useEffect(() => {
    // Skip saving during initial load or if already saving
    if (!initialLoadComplete || saving) return

    // Check if there are actual changes before saving
    const currentItemsString = JSON.stringify(findOutItems)
    const savedItemsString = JSON.stringify(lastSavedItems.current)

    if (currentItemsString === savedItemsString) return

    const saveTimeout = setTimeout(async () => {
      if (!project.id) {
        console.error('Cannot save research questions: Missing project ID')
        return
      }

      if (!project.product) {
        console.error('Cannot save research questions: Missing product ID in project')
        // Attempt to use selectedProduct as fallback if available
        if (selectedProduct && selectedProduct.id) {
          console.log('Using selectedProduct as fallback:', selectedProduct.id)
        } else {
          console.error('No product ID available, cannot save research questions')
          return
        }
      }

      try {
        setSaving(true)
        const productId = project.product || (selectedProduct ? selectedProduct.id : null)

        if (!productId) {
          console.error('No product ID available, cannot save research questions')
          return
        }

        console.log('Saving research questions with:', {
          projectId: project.id,
          productId,
          questions: findOutItems,
        })

        await customFetch('/updateResearchQuestions', 'PUT', {
          projectId: project.id,
          productId,
          questions: findOutItems,
        })
        lastSavedItems.current = [...findOutItems]
      } catch (error) {
        console.error('Error saving research questions:', error)
      } finally {
        setSaving(false)
      }
    }, 1000)

    return () => clearTimeout(saveTimeout)
    // customFetch is intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findOutItems, project.id, project.product, initialLoadComplete, saving, selectedProduct])

  const handleAddFindOutItem = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      e.preventDefault()
      if (newFindOutItem.trim()) {
        setFindOutItems([...findOutItems, newFindOutItem])
        setNewFindOutItem('')
      }
    }
  }

  const handleDeleteItem = (type, index) => {
    setFindOutItems((items) => items.filter((_, i) => i !== index))
  }

  const handleEditItem = (type, index, value) => {
    setEditingItem({ type, index, value })
  }

  const handleSaveEdit = (type, index) => {
    if (editingItem.value.trim()) {
      setFindOutItems((items) => {
        const newItems = [...items]
        newItems[index] = editingItem.value
        return newItems
      })
    }
    setEditingItem({ type: null, index: null, value: '', section: null })
  }

  const handleDragEnd = (result) => {
    if (!result.destination) return

    const { source, destination } = result
    const items = Array.from(findOutItems)
    const [reorderedItem] = items.splice(source.index, 1)
    items.splice(destination.index, 0, reorderedItem)
    setFindOutItems(items)
  }

  const handleSuggestScript = async () => {
    // Validate productId (required)
    const productId = project.product || (selectedProduct && selectedProduct.id)
    if (!productId) {
      toast.error('Missing product. Please select a product for this project first.')
      return
    }
    // Save current path for returning from Ask Anything
    localStorage.setItem('previousPath', window.location.pathname)

    // Format the things to find out as bullet points
    const bulletPoints = findOutItems.map((item) => `• ${item}`).join('\n')

    // Create the query
    const query = `Please suggest a non-leading user interview script for users of our product ${project.name}. Here are some things we're trying to find out:\n${bulletPoints}\nPlease follow best practices for user interview script design, inspired by such works as The Mom Test, Rochet Surgery Made Easy, and Continuous Discovery Habits.`

    // Navigate to Chats tab to keep navigation visible
    navigate('/dashboard/chats')

    await addToNewChatThread({
      productId,
      name: 'Interview Script: ' + project.name,
      message: query,
    })
  }

  const renderItem = (item, index, type) => {
    const isEditing = editingItem.type === type && editingItem.index === index

    return (
      <Draggable key={index} draggableId={`${type}-${index}`} index={index}>
        {(provided, snapshot) => (
          <li
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={`group bg-white p-4 rounded-lg shadow-sm border border-[#e4e4e4] hover:shadow-md transition-shadow ${
              snapshot.isDragging ? 'shadow-lg' : ''
            }`}
          >
            <div className="flex items-center gap-2">
              <div
                {...provided.dragHandleProps}
                className="cursor-grab opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <GripVertical size={16} className="text-[#62708d]" />
              </div>

              {isEditing ? (
                <input
                  type="text"
                  value={editingItem.value}
                  onChange={(e) => setEditingItem((prev) => ({ ...prev, value: e.target.value }))}
                  onBlur={() => handleSaveEdit(type, index)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit(type, index)}
                  className="flex-1 p-1 border border-[#e4e4e4] rounded focus:outline-none focus:ring-2 focus:ring-[#FF4A8E] focus:border-transparent"
                  autoFocus
                />
              ) : (
                <span className="flex-1 text-[#40424A] text-base font-bold font-['DM_Sans']">{item}</span>
              )}

              <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => handleEditItem(type, index, item)} className="p-1 hover:bg-grey-100 rounded">
                  <Edit2 size={16} className="text-[#62708d]" />
                </button>
                <button onClick={() => handleDeleteItem(type, index)} className="p-1 hover:bg-grey-100 rounded">
                  <Trash2 size={16} className="text-[#62708d]" />
                </button>
              </div>
            </div>
          </li>
        )}
      </Draggable>
    )
  }

  return (
    <div className="bg-white rounded-2xl p-6">
      <div className="py-[8px] border-b-2 border-grey-200 flex justify-between items-center">
        <p className="text-[#3f424a] text-4xl font-bold font-['DM_Sans'] leading-[41.40px]">🔍 Things to Find Out</p>
        <div className="flex items-center justify-center cursor-pointer group">
          <button
            onClick={handleSuggestScript}
            className="relative flex items-center justify-between bg-gradient-to-br from-gray-900 to-black/80 px-4 py-2 rounded-full shadow-2xl hover:shadow-xl transition-all 
            border border-white/10 
            ring-2 ring-black/20 
            hover:ring-black/30 
            hover:scale-105 
            active:scale-95 
            transform 
            duration-200 
            ease-in-out
            animate-breathing"
          >
            <div className="flex items-center gap-2">
              <MiniWaves size="h-6 w-6" />
              <span className="font-bold text-white text-sm">Suggest a Script</span>
            </div>
          </button>
        </div>
      </div>

      <div className="mt-[36px]">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="mt-6">
            <Droppable droppableId="findOut">
              {(provided) => (
                <ul ref={provided.innerRef} {...provided.droppableProps} className="space-y-3 mb-4">
                  {findOutItems.map((item, index) => renderItem(item, index, 'findOut'))}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>

            <div className="flex gap-2">
              <input
                type="text"
                value={newFindOutItem}
                onChange={(e) => setNewFindOutItem(e.target.value)}
                onKeyDown={handleAddFindOutItem}
                placeholder="Add a question..."
                className="flex-1 p-2 border border-[#e4e4e4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4A8E] focus:border-transparent"
              />
              <button
                onClick={handleAddFindOutItem}
                className="p-2 bg-[#FF4A8E] text-white rounded-lg hover:bg-[#ff3a7e] transition-colors"
              >
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </DragDropContext>
      </div>
    </div>
  )
}
